import React, { useEffect, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import "./publicArtistRegistration.css";

const CATEGORIES = [
  "DJ", "BEATBOXER", "RAPPER", "MODEL", "BOUNCER", "WRITER",
  "VIDEOGRAPHER", "PHOTOGRAPHER", "MUSIC PRODUCER", "FITNESS",
  "INFLUENCER", "DRUMMER", "GUITARIST", "BEAUTICIAN", "SINGER",
  "ARTIST", "CHOREOGRAPHER", "DIRECTOR", "PRODUCER",
  "INSTRUMENTALIST", "ACTOR", "ACTRESS", "MAGICIAN",
];

const DAYS = [
  { key: "sun", label: "S" },
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
];

const INITIAL_FORM = {
  phone: "",
  stageName: "",
  bandName: "",
  artistType: "Individual",
  categories: [],
  languageInput: "",
  languages: [],
  genres: "",
  story: "",
  experience: "1",
  totalGigs: "",
  crowdSize: "10-100",
  performanceNeeds: "",
  location: "",
  availableDays: [],
  feePerHour: "",
  bankName: "",
  accHolder: "",
  accNo: "",
  confirmAccNo: "",
  ifsc: "",
  branch: "",
  upi: "",
  acceptedTerms: false,
};

const STEP_TITLES = [
  "Build your artist identity",
  "Define your sound",
  "Tell your story",
  "Set the stage",
  "Finish your profile",
];

function cleanIndianPhone(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits) ? digits : "";
}

function ToggleGroup({ options, value, onChange }) {
  return (
    <div className="par-toggle-row">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={value === option ? "active" : ""}
          onClick={() => onChange(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function Field({ label, required, ...props }) {
  return (
    <label className="par-field">
      <span>{label}{required ? " *" : ""}</span>
      <input {...props} />
    </label>
  );
}

function PublicArtistRegistration() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [artistImage, setArtistImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [artistPreview, setArtistPreview] = useState("");
  const [selfiePreview, setSelfiePreview] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationModal, setVerificationModal] = useState("");
  const [existingArtistName, setExistingArtistName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const setValue = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
  };

  const toggleListValue = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: current[key].includes(value)
        ? current[key].filter((item) => item !== value)
        : [...current[key], value],
    }));
    setError("");
  };

  const addLanguage = () => {
    const language = form.languageInput.trim();
    if (!language) return;
    setForm((current) => ({
      ...current,
      languages: current.languages.includes(language)
        ? current.languages
        : [...current.languages, language],
      languageInput: "",
    }));
  };

  const chooseImage = (file, type) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose a valid image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Each image must be smaller than 8 MB.");
      return;
    }
    if (type === "artist") {
      if (artistPreview) URL.revokeObjectURL(artistPreview);
      setArtistImage(file);
      setArtistPreview(URL.createObjectURL(file));
    } else {
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      setSelfieImage(file);
      setSelfiePreview(URL.createObjectURL(file));
    }
    setError("");
  };

  const verifyRegisteredUser = async () => {
    const phone = cleanIndianPhone(form.phone);
    if (!phone) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const callable = httpsCallable(
        getFunctions(app, "asia-south1"),
        "verifyPublicArtistRegisteredUser"
      );
      const result = await callable({ phone });
      const registered = result?.data?.registered === true;
      const existingArtist = result?.data?.existingArtist || {};
      const alreadyArtist = existingArtist?.exists === true;
      const safeArtistName = String(existingArtist?.stageName || "").trim();
      setExistingArtistName(safeArtistName);
      setPhoneVerified(registered && !alreadyArtist);
      setVerificationModal(alreadyArtist ? "existing-artist" : registered ? "verified" : "unregistered");
    } catch (err) {
      setError("We could not verify this number right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const filePayload = async (file) => {
    const compressed = await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
      fileType: "image/jpeg",
    });
    const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
    return {
      contentType: "image/jpeg",
      base64: dataUrl.split(",")[1] || "",
    };
  };

  const currentErrors = () => {
    if (step === 0) {
      const errors = [];
      if (!form.stageName.trim()) errors.push("Enter your stage name");
      if (!artistImage) errors.push("Upload an artist picture");
      if (!form.categories.length) errors.push("Select at least one artist category");
      if (!form.languages.length) errors.push("Add at least one language");
      return errors;
    }
    if (step === 1) return form.genres.trim() ? [] : ["Enter your genres"];
    if (step === 2) return form.story.trim() ? [] : ["Tell us your story"];
    if (step === 3) {
      return [
        !form.performanceNeeds.trim() && "Enter your performance requirements",
        !form.location.trim() && "Enter your base location",
      ].filter(Boolean);
    }
    return [
      !form.availableDays.length && "Select at least one available day",
      !form.feePerHour.trim() && "Enter your performance fee",
      !selfieImage && "Upload a live selfie",
      !form.bankName.trim() && "Enter bank name",
      !form.accHolder.trim() && "Enter account holder name",
      !form.accNo.trim() && "Enter account number",
      !form.confirmAccNo.trim() && "Confirm account number",
      form.accNo.trim() !== form.confirmAccNo.trim() && "Account numbers do not match",
      !form.ifsc.trim() && "Enter IFSC code",
      !form.branch.trim() && "Enter branch name",
      !form.upi.trim() && "Enter UPI ID",
      !form.acceptedTerms && "Accept the Terms & Conditions and Privacy Policy",
    ].filter(Boolean);
  };

  const next = () => {
    const errors = currentErrors();
    if (errors.length) {
      setError(errors.join(". "));
      return;
    }
    if (step < 4) setStep((current) => current + 1);
    else submit();
  };

  const submit = async () => {
    const phone = cleanIndianPhone(form.phone);
    if (!phoneVerified || !phone) {
      setError("Please verify your registered ApnaArtist mobile number again.");
      setStep(0);
      return;
    }

    setBusy(true);
    setError("");
    try {
      const [artistImagePayload, selfieImagePayload] = await Promise.all([
        filePayload(artistImage),
        filePayload(selfieImage),
      ]);
      const callable = httpsCallable(
        getFunctions(app, "asia-south1"),
        "submitPublicArtistRegistration",
        { timeout: 120000 }
      );
      await callable({
        phone,
        stageName: form.stageName.trim(),
        bandName: form.bandName.trim(),
        artistType: form.artistType,
        artistCategories: form.categories,
        languages: form.languages,
        genres: form.genres.trim(),
        story: form.story.trim(),
        experience: form.experience,
        totalGigs: form.totalGigs.trim(),
        crowdSize: form.crowdSize,
        performanceNeeds: form.performanceNeeds.trim(),
        location: form.location.trim(),
        availableDays: form.availableDays.map((key) => DAYS.find((day) => day.key === key)?.label),
        feePerHour: form.feePerHour.trim(),
        artistImage: artistImagePayload,
        selfieImage: selfieImagePayload,
        bank: {
          bankName: form.bankName.trim(),
          accHolder: form.accHolder.trim(),
          accNo: form.accNo.trim(),
          confirmAccNo: form.confirmAccNo.trim(),
          ifsc: form.ifsc.trim().toUpperCase(),
          branch: form.branch.trim(),
          upi: form.upi.trim(),
        },
      });
      setSuccess(true);
    } catch (err) {
      console.error("Artist registration failed:", err?.code || err?.message || "unknown");
      setError("We could not submit your profile. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (artistPreview) URL.revokeObjectURL(artistPreview);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setForm(INITIAL_FORM);
    setArtistImage(null);
    setSelfieImage(null);
    setArtistPreview("");
    setSelfiePreview("");
    setPhoneVerified(false);
    setVerificationModal("");
    setExistingArtistName("");
    setError("");
    setSuccess(false);
    setStep(0);
  };

  return (
    <main className="par-page" ref={topRef}>
      <section className="par-shell">
        <header className="par-hero">
          <img src="/brand/apnaartist-profile.png" alt="ApnaArtist" />
          <div>
            <span className="par-eyebrow">Artist registration</span>
            <h1>{phoneVerified ? STEP_TITLES[step] : "Verify your mobile"}</h1>
            <p>{phoneVerified ? `Step ${step + 1} of 5` : "Secure registration access"}</p>
          </div>
        </header>

        {phoneVerified && (
          <div className="par-progress" aria-label={`Step ${step + 1} of 5`}>
            <span style={{ width: `${((step + 1) / 5) * 100}%` }} />
          </div>
        )}

        <div className="par-content">
          {!phoneVerified && (
            <section className="par-section par-verification-gate">
              <h2><span>01</span> Verify your mobile</h2>
              <p className="par-lead">
                Enter the mobile number registered with your ApnaArtist user account.
                Artist registration will open after successful verification.
              </p>
              <div className="par-phone-row">
                <Field
                  label="Registered mobile number"
                  required
                  value={form.phone}
                  inputMode="numeric"
                  maxLength={13}
                  autoComplete="tel"
                  placeholder="7002618625"
                  onChange={(event) => setValue("phone", event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") verifyRegisteredUser();
                  }}
                />
                <button type="button" className="par-small-button" onClick={verifyRegisteredUser} disabled={busy}>
                  {busy ? "Checking..." : "Verify number"}
                </button>
              </div>
              <p className="par-gate-note">
                Only existing registered ApnaArtist users can continue.
              </p>
            </section>
          )}

          {phoneVerified && step === 0 && (
            <>
              <section className="par-section">
                <p className="par-verified">Registered ApnaArtist user verified</p>
                <h2><span>01</span> Profile basics</h2>
                <Field label="Stage name" required value={form.stageName} onChange={(e) => setValue("stageName", e.target.value)} />
                <Field label="Band name (optional)" value={form.bandName} onChange={(e) => setValue("bandName", e.target.value)} />
                <label className="par-upload">
                  {artistPreview ? <img src={artistPreview} alt="Artist preview" /> : <strong>＋</strong>}
                  <span>{artistImage ? "Change artist picture" : "Upload artist picture *"}</span>
                  <input type="file" accept="image/*" onChange={(e) => chooseImage(e.target.files?.[0], "artist")} />
                </label>
              </section>

              <section className="par-section">
                <h2><span>02</span> Artist categories</h2>
                <div className="par-chips">
                  {CATEGORIES.map((category) => (
                    <button
                      type="button"
                      key={category}
                      className={form.categories.includes(category) ? "active" : ""}
                      onClick={() => toggleListValue("categories", category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                <h3>Artist type</h3>
                <ToggleGroup options={["Individual", "Group"]} value={form.artistType} onChange={(value) => setValue("artistType", value)} />
              </section>

              <section className="par-section">
                <h2><span>03</span> Languages</h2>
                <div className="par-add-row">
                  <Field label="Language" value={form.languageInput} placeholder="e.g. Hindi" onChange={(e) => setValue("languageInput", e.target.value)} />
                  <button type="button" className="par-small-button" onClick={addLanguage}>Add</button>
                </div>
                <div className="par-tags">
                  {form.languages.map((language) => (
                    <button type="button" key={language} onClick={() => toggleListValue("languages", language)}>
                      {language} ×
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}

          {phoneVerified && step === 1 && (
            <section className="par-section par-tall">
              <h2><span>02</span> Genres</h2>
              <p className="par-lead">What kind of sound or performance do you bring to the stage?</p>
              <Field label="Genres (comma separated)" required value={form.genres} placeholder="Bollywood, Indie, Sufi" onChange={(e) => setValue("genres", e.target.value)} />
            </section>
          )}

          {phoneVerified && step === 2 && (
            <section className="par-section par-tall">
              <h2><span>03</span> Your story</h2>
              <p className="par-lead">Help audiences and event organisers understand what makes you special.</p>
              <label className="par-field">
                <span>Tell us about yourself *</span>
                <textarea rows="9" value={form.story} onChange={(e) => setValue("story", e.target.value)} />
              </label>
            </section>
          )}

          {phoneVerified && step === 3 && (
            <>
              <section className="par-section">
                <h2><span>04</span> Experience</h2>
                <ToggleGroup options={["1", "5+", "10+"]} value={form.experience} onChange={(value) => setValue("experience", value)} />
                <Field label="Total gigs (optional)" inputMode="numeric" value={form.totalGigs} onChange={(e) => setValue("totalGigs", e.target.value)} />
              </section>
              <section className="par-section">
                <h2><span>05</span> Crowd handled</h2>
                <ToggleGroup options={["10-100", "100-500", "500-1L+"]} value={form.crowdSize} onChange={(value) => setValue("crowdSize", value)} />
              </section>
              <section className="par-section">
                <h2><span>06</span> Performance details</h2>
                <Field label="Performance requirements" required value={form.performanceNeeds} onChange={(e) => setValue("performanceNeeds", e.target.value)} />
                <Field label="Base location" required value={form.location} placeholder="City, State" onChange={(e) => setValue("location", e.target.value)} />
              </section>
            </>
          )}

          {phoneVerified && step === 4 && (
            <>
              <section className="par-section">
                <h2><span>07</span> Availability</h2>
                <div className="par-days">
                  {DAYS.map((day) => (
                    <button type="button" key={day.key} className={form.availableDays.includes(day.key) ? "active" : ""} onClick={() => toggleListValue("availableDays", day.key)}>
                      {day.label}
                    </button>
                  ))}
                </div>
                <Field label="Performance fee per hour (₹)" required inputMode="numeric" value={form.feePerHour} onChange={(e) => setValue("feePerHour", e.target.value)} />
              </section>
              <section className="par-section">
                <h2><span>08</span> Live selfie</h2>
                <label className="par-upload wide">
                  {selfiePreview ? <img src={selfiePreview} alt="Selfie preview" /> : <strong>＋</strong>}
                  <span>{selfieImage ? "Retake or change selfie" : "Take or upload live selfie *"}</span>
                  <input type="file" accept="image/*" capture="user" onChange={(e) => chooseImage(e.target.files?.[0], "selfie")} />
                </label>
              </section>
              <section className="par-section">
                <h2><span>09</span> Bank details</h2>
                <Field label="Bank name" required value={form.bankName} onChange={(e) => setValue("bankName", e.target.value)} />
                <Field label="Account holder name" required value={form.accHolder} onChange={(e) => setValue("accHolder", e.target.value)} />
                <Field label="Account number" required inputMode="numeric" value={form.accNo} onChange={(e) => setValue("accNo", e.target.value.replace(/\D/g, ""))} />
                <Field label="Confirm account number" required inputMode="numeric" value={form.confirmAccNo} onChange={(e) => setValue("confirmAccNo", e.target.value.replace(/\D/g, ""))} />
                <Field label="IFSC code" required value={form.ifsc} onChange={(e) => setValue("ifsc", e.target.value.toUpperCase())} />
                <Field label="Branch name" required value={form.branch} onChange={(e) => setValue("branch", e.target.value)} />
                <Field label="UPI ID" required value={form.upi} onChange={(e) => setValue("upi", e.target.value)} />
                <label className="par-consent">
                  <input type="checkbox" checked={form.acceptedTerms} onChange={(e) => setValue("acceptedTerms", e.target.checked)} />
                  <span>I agree to ApnaArtist&apos;s Terms &amp; Conditions and Privacy Policy.</span>
                </label>
              </section>
            </>
          )}

          {error && <div className="par-error" role="alert">{error}</div>}
        </div>

        {phoneVerified && (
          <footer className="par-footer">
            {step > 0 && <button type="button" className="par-back" onClick={() => setStep((current) => current - 1)} disabled={busy}>Back</button>}
            <button type="button" className="par-next" onClick={next} disabled={busy}>
              {busy ? "Please wait..." : step < 4 ? "Next · Keep Shining" : "Complete Profile"}
            </button>
          </footer>
        )}
      </section>

      {success && (
        <div className="par-modal" role="dialog" aria-modal="true">
          <div className="par-success">
            <div className="par-success-icon">✓</div>
            <span>Profile submitted</span>
            <h2>Congratulations!</h2>
            <p>Your artist profile is now with our verification team. Review may take up to 72 hours.</p>
            <button type="button" onClick={reset}>Register another artist</button>
          </div>
        </div>
      )}
      {verificationModal && (
        <div className="par-modal" role="dialog" aria-modal="true">
          <div className={`par-success ${verificationModal !== "verified" ? "not-registered" : ""}`}>
            <div className="par-success-icon">{verificationModal === "verified" ? "✓" : "!"}</div>
            <span>
              {verificationModal === "verified"
                ? "Number verified"
                : verificationModal === "existing-artist"
                  ? "Artist already exists"
                  : "Registration required"}
            </span>
            <h2>{verificationModal === "verified" ? "You’re all set" : "Join ApnaArtist first"}</h2>
            <p>
              {verificationModal === "verified"
                ? "This mobile number is registered with ApnaArtist. You can now complete your artist profile."
                : verificationModal === "existing-artist"
                  ? `${existingArtistName || "This user"} already has an artist account with ApnaArtist. Please use that artist profile instead of creating a new one.`
                  : "We could not find this mobile number in our registered user base. Please install the ApnaArtist app and create your user account first."}
            </p>
            {verificationModal === "verified" ? (
              <button type="button" onClick={() => setVerificationModal("")}>Continue registration</button>
            ) : verificationModal === "existing-artist" ? (
              <button type="button" className="par-modal-secondary" onClick={() => setVerificationModal("")}>Use another number</button>
            ) : (
              <>
                <a className="par-store-button" href="https://play.google.com/store/apps/details?id=com.apnaartist2.app" target="_blank" rel="noreferrer">
                  Download ApnaArtist
                </a>
                <button type="button" className="par-modal-secondary" onClick={() => setVerificationModal("")}>Try another number</button>
              </>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default PublicArtistRegistration;
